/**
 * SharedViewPage
 *
 * Public page for recipients of a shared list link.
 * Accessible at /shared/:shareId — no authentication required.
 *
 * Authenticated users are redirected to the dashboard with the shared list selected.
 * Unauthenticated users see the task list with optional toggle capability.
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';

interface SharedTask {
  id: string;
  content: string;
  isCompleted: boolean;
}

interface SharedViewData {
  listId: string;
  name: string;
  ownerName: string;
  linkPermission: 'viewer' | 'checker' | 'member' | null;
  tasks: SharedTask[];
}

async function getSharedView(token: string): Promise<SharedViewData> {
  const res = await fetch(`/api/v1/shared/${token}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error('Failed to load'), { status: res.status, ...err });
  }
  return res.json() as Promise<SharedViewData>;
}

async function toggleTask(token: string, taskId: string, isCompleted: boolean): Promise<void> {
  const res = await fetch(`/api/v1/shared/${token}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isCompleted }),
  });
  if (!res.ok) throw new Error('Failed to update task');
}

export function SharedViewPage(): React.ReactElement {
  const { shareId: token } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery<SharedViewData>({
    queryKey: ['shared-view', token],
    queryFn: () => getSharedView(token!),
    // Skip polling once authenticated — redirect will fire immediately after data arrives
    refetchInterval: isAuthenticated ? false : 30_000,
  });

  // Redirect authenticated users to the dashboard with the shared list selected
  useEffect(() => {
    if (!authLoading && isAuthenticated && data?.listId) {
      navigate(`/app?view=shared:${data.listId}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, data?.listId, navigate]);

  const toggle = useMutation({
    mutationFn: ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) => {
      if (!token) return Promise.reject(new Error('No share token'));
      return toggleTask(token, taskId, isCompleted);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-view', token] }),
  });

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading shared list…
      </div>
    );
  }

  if (error) {
    const status = (error as { status?: number }).status;
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 text-center p-8">
        <h1 className="text-2xl font-bold">
          {status === 404 ? 'List not found' : 'Unable to load list'}
        </h1>
        <p className="text-gray-500">The shared link may be invalid, expired, or revoked.</p>
        <a href="https://askdanny.to" className="text-blue-600 hover:underline">
          Try Danny free →
        </a>
      </div>
    );
  }

  if (!data) return <></>;

  // Authenticated users are being redirected — show nothing while navigating
  if (isAuthenticated) return <></>;

  const { name, ownerName, linkPermission, tasks } = data;
  const canToggle = linkPermission === 'checker' || linkPermission === 'member';

  return (
    <div className="max-w-lg mx-auto p-4 min-h-screen">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{name}</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm">Shared by {ownerName}</p>
      </header>

      <ul className="space-y-2">
        {tasks.map((task) => (
          <li key={task.id} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {canToggle ? (
              <input
                type="checkbox"
                checked={task.isCompleted}
                onChange={(e) => toggle.mutate({ taskId: task.id, isCompleted: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 cursor-pointer"
              />
            ) : (
              <span
                className={`w-5 h-5 rounded border border-gray-300 dark:border-gray-600 shrink-0 ${task.isCompleted ? 'bg-gray-200 dark:bg-gray-600' : ''}`}
              />
            )}
            <span className={task.isCompleted ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}>
              {task.content}
            </span>
          </li>
        ))}
        {tasks.length === 0 && (
          <p className="text-gray-400 dark:text-gray-500 text-center py-8">No tasks in this list.</p>
        )}
      </ul>

      <footer className="mt-8 text-center text-sm text-gray-400 dark:text-gray-500">
        Shared via{' '}
        <a href="https://askdanny.to" className="text-blue-500 hover:underline">
          Danny
        </a>
        {linkPermission === 'viewer' && ' · View only'}
      </footer>
    </div>
  );
}
